import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { FileDownloadOutlined as DownloadIcon } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { StyledButton } from '../../../components/common/StyledButton';  
import { baseUrls } from '../../../constants/module-constants';
import { useAuthContext } from '../../../context/auth-context';
import AffiliationFilter from '../../../components/reports/AffiliationFilter';
import { getAffiliationZones, filterByAffiliationZone } from '../../../utils/report-filters';
import { authFetch } from '../../../lib/http';

const GeneralGradesCertificatesPage = () => {
  const { activeCamp } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [clubsData, setClubsData] = useState([]);
  const [zoneFilter, setZoneFilter] = useState('');

  useEffect(() => {
    if (!activeCamp?._id) {
      setClubsData([]);
      return;
    }
    fetchGrades(activeCamp._id);
  }, [activeCamp?._id]);

  // Al cambiar de campamento las zonas son otras — limpiamos el filtro
  useEffect(() => { setZoneFilter(''); }, [activeCamp?._id]);

  const zones = getAffiliationZones(clubsData);
  const visibleClubs = filterByAffiliationZone(clubsData, zoneFilter);

  const fetchGrades = async (campId) => {
    setLoading(true);
    try {
      const response = await authFetch(`${baseUrls.baseUrl}/reports/allgradesbyclub?campId=${campId}`);
      const data = await response.json();
      setClubsData(data.results);
    } catch (error) {
      console.error('Error fetching grades:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToPdf = async () => {
    const zip = new JSZip();

    visibleClubs.forEach((club) => {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      let yPosition = 10;

      // Club header
      doc.setFontSize(14);
      doc.text(`Club: ${club.clubName}`, 10, yPosition);
      yPosition += 7;
      doc.text(`Zona: ${club.affiliationZone}`, 10, yPosition);
      yPosition += 10;

      // Build table rows: events -> rubrics
      const tableData = [];
      club.events.forEach((event) => {
        event.rubrics.forEach((rubric, rubricIndex) => {
          tableData.push([
            rubricIndex === 0 ? event.name : '',
            rubric.name,
            rubric.grade,
            rubric.comments || '',
            rubric.saved_by || '',
          ]);
        });

        // Total row per event
        tableData.push([
          { content: 'Total', styles: { fontStyle: 'bold', fillColor: [241, 241, 241] } },
          { content: `Total de Rubrics: ${event.rubrics.length}`, styles: { fontStyle: 'bold', fillColor: [241, 241, 241] } },
          { content: event.categoryTotal?.toString() ?? '', styles: { fontStyle: 'bold', fillColor: [241, 241, 241] } },
          { content: '', styles: { fillColor: [241, 241, 241] } },
          { content: '', styles: { fillColor: [241, 241, 241] } },
        ]);
      });

      autoTable(doc, {
        startY: yPosition,
        head: [['Eventos', 'Criterios de Evaluación', 'Puntos', 'Comentarios', 'Evaluado Por']],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 45 },
          2: { cellWidth: 20 },
          3: { cellWidth: 55 },
          4: { cellWidth: 35 },
        },
      });

      const fileName = `${club.affiliationZone}_${club.clubName}.pdf`;
      zip.file(fileName, doc.output('blob'));
    });

    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, 'certificados-calificaciones-generales.zip');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Certificados de Calificaciones Generales</Typography>
        <StyledButton
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={exportToPdf}
        >
          Generar PDF
        </StyledButton>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <AffiliationFilter value={zoneFilter} onChange={setZoneFilter} zones={zones} />
        </Grid>
        <Grid item xs={12} sm={6} md={9}>
          <Typography variant="body2" color="text.secondary">
            {visibleClubs.length} de {clubsData.length} clubes
            {zoneFilter ? ` · ${zoneFilter}` : ''}
          </Typography>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        visibleClubs.map((club, clubIndex) => (
          <Card key={clubIndex} sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                <strong>Club:</strong> {club.clubName}
              </Typography>
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 3 }}>
                <strong>Zona:</strong> {club.affiliationZone}
              </Typography>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>Eventos</strong></TableCell>
                      <TableCell><strong>Criterios de Evaluación</strong></TableCell>
                      <TableCell><strong>Puntos</strong></TableCell>
                      <TableCell><strong>Comentarios</strong></TableCell>
                      <TableCell><strong>Evaluado Por</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {club.events.map((event, eventIndex) => (
                      <React.Fragment key={eventIndex}>
                        {event.rubrics.map((rubric, rubricIndex) => (
                          <TableRow key={`${eventIndex}-${rubricIndex}`}>
                            {rubricIndex === 0 && (
                              <TableCell rowSpan={event.rubrics.length}>
                                {event.name}
                              </TableCell>
                            )}
                            <TableCell>{rubric.name}</TableCell>
                            <TableCell>{rubric.grade}</TableCell>
                            <TableCell>{rubric.comments}</TableCell>
                            <TableCell>{rubric.saved_by}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ backgroundColor: '#f1f1f1', fontWeight: 'bold' }}>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell>
                            <strong>Total de Eventos: {event.rubrics.length}</strong>
                          </TableCell>
                          <TableCell><strong>{event.categoryTotal}</strong></TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ))
      )}
    </Container>
  );
};

export default GeneralGradesCertificatesPage;